import { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { cn } from '../lib/utils';
import { PLATFORM_VERSION, RELEASE_DATE, RELEASE_HISTORY } from '../config/version';
import {
  Rocket, Bug, Wrench, ChevronDown, ChevronUp, Calendar, Tag, Shield,
  CheckCircle2, ArrowUpCircle, Star
} from 'lucide-react';

const TYPE_CONFIG = {
  feature: { label: 'Feature Release', icon: Rocket, color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  fix: { label: 'Bug Fix & Stability', icon: Bug, color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  improvement: { label: 'Improvement', icon: Wrench, color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  security: { label: 'Security Update', icon: Shield, color: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

function formatDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function ReleaseCard({ release, isLatest }) {
  const [expanded, setExpanded] = useState(isLatest);
  const typeConf = TYPE_CONFIG[release.type] || TYPE_CONFIG.feature;
  const TypeIcon = typeConf.icon;

  return (
    <div className="relative" data-testid={`release-${release.version}`}>
      {/* Timeline connector */}
      <div className="absolute left-5 top-0 bottom-0 w-px bg-gray-200 -z-10" />

      <div className="flex gap-4">
        {/* Timeline dot */}
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 z-10",
          isLatest
            ? "bg-emerald-500 border-emerald-500 text-white"
            : "bg-white border-gray-300 text-gray-400"
        )}>
          {isLatest ? <Star className="h-4 w-4" /> : <TypeIcon className="h-4 w-4" />}
        </div>

        {/* Content */}
        <Card className={cn(
          "flex-1 mb-4 transition-all duration-200",
          isLatest ? "border-emerald-200 shadow-sm shadow-emerald-500/5" : "border-gray-200"
        )}>
          <CardContent className="p-4">
            {/* Header */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-start justify-between gap-3 text-left"
              data-testid={`release-toggle-${release.version}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-base font-bold text-gray-900">v{release.version}</span>
                  {isLatest && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px]">
                      Current
                    </Badge>
                  )}
                  <Badge className={cn("text-[9px] border", typeConf.color)}>
                    {typeConf.label}
                  </Badge>
                </div>
                <h3 className="text-sm font-semibold text-gray-800">{release.title}</h3>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400">
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(release.date)}</span>
                  <span className="flex items-center gap-1"><Tag className="h-3 w-3" />{release.highlights.length} changes</span>
                </div>
              </div>
              <div className="text-gray-400 mt-1">
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </button>

            {/* Expanded content */}
            {expanded && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <ul className="space-y-2">
                  {release.highlights.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function ReleaseNotesPage() {
  return (
    <div className="space-y-4 animate-fadeIn" data-testid="release-notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
            <ArrowUpCircle className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Release Notes</h1>
            <p className="text-xs text-gray-400">Platform version history and changelog</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs px-3 py-1" data-testid="current-version-badge">
            v{PLATFORM_VERSION}
          </Badge>
          <span className="text-xs text-gray-400">{formatDate(RELEASE_DATE)}</span>
        </div>
      </div>

      {/* Current Version Summary */}
      <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50/50 to-white" data-testid="current-version-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Rocket className="h-6 w-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Konnect v{PLATFORM_VERSION}</p>
              <p className="text-xs text-gray-500">{RELEASE_HISTORY[0]?.title || 'Latest Release'}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{RELEASE_HISTORY[0]?.highlights.length || 0} updates in this release</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <div className="pl-0">
        {RELEASE_HISTORY.map((release, idx) => (
          <ReleaseCard key={release.version} release={release} isLatest={idx === 0} />
        ))}

        {/* Timeline end */}
        <div className="flex gap-4 items-center">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 border-2 border-gray-200 text-gray-300">
            <Rocket className="h-4 w-4" />
          </div>
          <p className="text-xs text-gray-400">Platform inception</p>
        </div>
      </div>
    </div>
  );
}
